import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { formatEUR } from "@/lib/dict";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const PERIOD_LABELS = { month: "30 derniers jours", quarter: "90 derniers jours", year: "12 derniers mois" };

function DeltaBadge({ delta, inverse = false }) {
  if (delta === null || delta === undefined) return <span className="text-xs text-slate-400">—</span>;
  const isUp = delta > 0;
  // inverse=true means lower is better (e.g. defects, costs)
  const bad = inverse ? isUp : !isUp;
  const zero = delta === 0;
  const Icon = zero ? Minus : isUp ? TrendingUp : TrendingDown;
  const color = zero ? "text-slate-500 bg-slate-100" : bad ? "text-red-700 bg-red-50" : "text-emerald-700 bg-emerald-50";
  const sign = delta > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${color}`}>
      <Icon size={11} />{sign}{delta}%
    </span>
  );
}

function CompareRow({ label, current, previous, delta_pct, inverse, formatter }) {
  const fmt = formatter || ((v) => v);
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-500">Précédente : {fmt(previous)}</div>
      </div>
      <div className="text-right">
        <div className="font-display font-bold text-lg text-slate-900">{fmt(current)}</div>
        <DeltaBadge delta={delta_pct} inverse={inverse} />
      </div>
    </div>
  );
}

export default function CompareStats({ period = "month", onPeriodChange }) {
  const { data } = useQuery({ queryKey: ["stats-compare", period], queryFn: () => api.get(`/stats/compare?period=${period}`).then((r) => r.data) });
  if (!data) return null;
  return (
    <Card className="p-5 border-slate-200">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Comparateur temporel</div>
          <h3 className="font-display text-lg font-semibold text-slate-900 mt-0.5">Période N vs N-1</h3>
        </div>
        <select
          data-testid="compare-period"
          value={period}
          onChange={(e) => onPeriodChange?.(e.target.value)}
          className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1.5 font-medium"
        >
          {Object.entries(PERIOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div data-testid="compare-rows">
        <CompareRow label="Désordres créés" {...data.defects} inverse />
        <CompareRow label="Sinistres créés" {...data.claims} inverse />
        <CompareRow label="Coût estimé (désordres)" {...data.estimated_cost} inverse formatter={formatEUR} />
        <CompareRow label="Indemnisation reçue" {...data.compensation} formatter={formatEUR} />
      </div>
    </Card>
  );
}
